import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { GetJson, PutJson } from '@awp/helpers/fetchReact';

import { AWPLocalDB } from '@components/Common/AWPLocalDB';
import { OfflineFormStatus, FORM_MODE, EventTypes, REST_API, NULL_OPTION } from '@awp/constants';

import {
  getAvalancheObservations,
  getAvalancheGeographicAreas,
  getAvalancheGeographicPaths,
} from '@stores/avalancheSlice';
import { selectCurrentUser } from '@stores/usersSlice';
import { selectAvalancheTechnicians } from '@stores/dropdownsSlice';
import { getEventPropertiesByTypeName } from '@stores/eventSlice';

import { uuidv4 } from '@awp/helpers/Utils';
import { isValueEmpty, getUtcToLocal } from '@awp/helpers/utilsReact';

import { Row, Col, Card, Button, Spinner, Container } from 'react-bootstrap';
import * as Yup from 'yup';

import {
  FormTextArea,
  FormRadio,
  FormRadioGroup,
  FormSelect,
  FormDatePicker,
  FormCheckbox,
  FormNumberInput,
  DropdownWithGeolocate,
  FormTextReadOnly,
} from '@components/Common/AwpFormInputs';

import { OccurrenceOptionalFields } from './OptionalFields';
import { mapFormToServerFormat, mapServerToFormFormat, mapObject, HaversineDistance } from './OccurrenceHelper';
import { defaultValidations, generateOptionalValidation } from './OccurrenceValidation';
import { selectEventTypes } from '@awp/stores/eventSlice';

import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import AwpValidationErrorModal from '@components/Common/AwpValidationErrorModal';
import { useAvalancheEventContext } from './AvalanceEventContext';
import AwpYesNoModal from '@components/Common/AwpYesNoModal';
import ProcessedCriticalFieldsEdit from './ProcessedCriticalFieldsEdit';
import {
  checkEventType,
  findEventTypeNmById,
  isCriticalFieldsUpdated,
  locationCriticalFieldsToEntity,
} from './ProcessedCriticalFieldsHelper';
import AwpYesNoDialog from '@components/Common/AwpYesNoDialog';

const OccurrenceEdit = ({
  setMode,
  mode,
  setEventId,
  eventId,
  setLoading,
  loading,
  fullData,
  setEventTypeToActOn,
  isProcessed,
  isRoute,
  isDuplicated,
  isFromControl,
  fromMenu,
  findEvent,
  breadcrumbName,
}) => {
  let fullContextData;
  try {
    fullContextData = useAvalancheEventContext()?.fullContextData;
  } catch (e) {
    fullContextData = null;
  }

  const loadedData = fullContextData ?? fullData;

  const currentUser = useSelector(selectCurrentUser);
  const location = useLocation();
  const navigate = useNavigate();
  const triggerTypes = useSelector((state) => getAvalancheObservations(state, 'aval_trig_type_cd'));
  const programAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalancheAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA'));
  const avalanchePaths = useSelector((state) => getAvalancheGeographicPaths(state));
  const avalancheTechs = useSelector((state) => selectAvalancheTechnicians(state));
  const avalancheEventProperties = useSelector((state) => getEventPropertiesByTypeName(state, 'AVALANCHE EVENT'));
  const eventTypes = useSelector(selectEventTypes);
  const observationFields = avalancheEventProperties.map((x) => x.ObservationName);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showRoadImpactMessage, setShowRoadImpactMessage] = useState(false);

  const nonExplosiveTriggerTypes = triggerTypes.filter((t) => !t.value.startsWith('X'));
  // need to filter based on selected control type and R & Y
  const explosiveTriggerTypes = triggerTypes.filter((t) => t.value.startsWith('X'));

  const avalancheSize = avalancheEventProperties.find((x) => x.ObservationName == 'aval_size_cd');
  let avalancheSizeOptions = [];
  if (avalancheSize != null) {
    const options = [...avalancheSize.ObservationValues];
    avalancheSizeOptions = options
      .filter((x) => x.EndTimestamp == null)
      .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
      .map((value) => {
        let label = `${value.ObservedCharacterValue}`;
        if (value.DisplayedMeaning) {
          label += ` (${value.DisplayedMeaning})`;
        }
        return {
          label: label,
          value: value.ObservedCharacterValue,
        };
      });
  }

  const groupTrigTypes = (trigTypes) => {
    let groupChar = '';
    const groups = [];
    let group;

    trigTypes.forEach((x, index) => {
      if (groupChar !== x.value.charAt(0)) {
        groupChar = x.value.charAt(0);
        group = { label: getGroupLabel(groupChar), index, options: [] };
        groups.push(group);
      }
      group.options.push(x);
    });
    return groups;
  };

  const getGroupLabel = (groupChar) => {
    switch (groupChar) {
      case 'N':
        return 'Natural Triggers';
      case 'H':
        return 'Artificial Triggers, helicopters';
      case 'V':
        return 'Over-snow vehicles (other than snowmobile)';
      case 'S':
        return 'Artificial Triggers, skiers, etc';
      case 'M':
        return 'Artificial Triggers, snowmobiles';
      case 'O':
        return 'Artificial Triggers (miscellaneous)';
      case 'X':
        return 'Explosive Triggers';
      default:
        return 'Unknown Triggers';
    }
  };

  const [explosiveControlEvent, setExplosiveControlEvent] = useState(false);

  const [occurrenceData, setOccurrenceData] = useState({});
  const [occurrenceErrors, setOccurrenceErrors] = useState(null);
  const [formStatus, setFormStatus] = useState(mode !== FORM_MODE.CREATE ? OfflineFormStatus.Valid : null);

  const avalanchePrograms = mode === FORM_MODE.CREATE ? programAreas.filter((x) => !x.isDisabled) : programAreas;
  const [areaList, setAreaList] = useState(
    mode === FORM_MODE.CREATE ? avalancheAreas.filter((x) => !x.isDisabled) : avalancheAreas,
  );
  const [pathsList, setPathsList] = useState(
    mode === FORM_MODE.CREATE ? avalanchePaths.filter((x) => !x.isDisabled) : avalanchePaths,
  );
  const [isAvalancheTech, setIsAvalancheTech] = useState(true);
  const [isTrigDistReq, setIsTrigDistReq] = useState(false);

  const readOnly = mode !== FORM_MODE.EDIT && mode !== FORM_MODE.CREATE;
  const buttonLabel = 'Save';
  const occurrenceType = EventTypes.AvalancheOccurrence;
  const processedLocationValidations = () => {
    let schema = Yup.object({
      avalanche_path: Yup.object()
        .label('Path')
        .nullable()
        .test('Path', 'Avalanche Path is required', (value, context) => {
          if (context.parent.avalanche_path && context.parent.avalanche_path.value) return true;
          return false;
        }),
      avalanche_area: Yup.object()
        .label('Avalanche Area')
        .nullable()
        .test('AvalancheArea', 'Avalanche Area is required', (value, context) => {
          if (context.parent.avalanche_area && context.parent.avalanche_area.value) return true;
          return false;
        }),
    });

    return schema;
  };
  const optionalValidation = generateOptionalValidation(avalancheEventProperties);
  const defaultValidation = defaultValidations();
  const validationSchema =
    isProcessed && mode === FORM_MODE.EDIT
      ? defaultValidation.concat(optionalValidation).concat(processedLocationValidations())
      : defaultValidation.concat(optionalValidation);
  const [originalValues, setOriginalValues] = useState(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showCriticalFieldsModal, setShowCriticalFieldsModal] = useState(false);
  const [showCriticalFields, setShowCriticalFields] = useState(false);
  const [criticalFields, setCriticalFields] = useState(null);
  const [loadingCriticalField, setLoadingCriticalField] = useState(false);
  const [eventPaths, setEventPaths] = useState(null); // e.g. RC or Phased events
  const [eventSegment, setEventSegment] = useState(null);
  const [eventFrom, setEventFrom] = useState(null);
  const [eventTo, setEventTo] = useState(null);
  const [eventLocations, setEventLocations] = useState(null); // == non-eventPaths e.g. AC or AO
  const nullOption = NULL_OPTION;

  const getPosition = (e, setFieldValue, setFieldTouched) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const crd = pos.coords;

        //HaversineDistance = (lat1, long1, lat2, long2)
        const pathsWithDistance = avalanchePaths
          .map((p) => {
            const dist = HaversineDistance(p.latitude, p.longitude, crd.latitude, crd.longitude);
            return { ...p, label: `${p.label} (${parseFloat(dist).toFixed(3).toString()}km)`, distanceAway: dist };
          })
          .sort((a, b) => {
            return a.distanceAway - b.distanceAway;
          });

        setPathsList(pathsWithDistance);
        const closestPath = pathsWithDistance.reduce((p, c) => {
          return p.distanceAway < c.distanceAway ? p : c;
        });

        if (closestPath) {
          setFieldValue('avalanche_path', closestPath);
          setFieldTouched('avalanche_path', true, true);
          pathOnChange(closestPath, setFieldValue, pathsWithDistance);
        }
      },
      (err) => {
        console.warn(`ERROR(${err.code}): ${err.message}`);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      },
    );
  };

  const title =
    mode === FORM_MODE.EDIT
      ? isProcessed
        ? 'Edit Processed Avalanche Observations'
        : 'Edit Avalanche Observations'
      : mode === FORM_MODE.CREATE
      ? isProcessed
        ? 'Create Processed Avalanche Observations'
        : 'Create Avalanche Observations'
      : isProcessed
      ? 'Processed Avalanche Observations'
      : 'Avalanche Observations';

  const filterList = (list, value) => {
    return list.filter(({ parentId }) => {
      return parentId === value;
    });
  };

  useEffect(() => {
    if (mode === FORM_MODE.CREATE || mode === FORM_MODE.EDIT) {
      setIsAvalancheTech(avalancheTechs.options.findIndex((t) => t.value === currentUser.SecurityIdentifierId) !== -1);
    }
    if (mode === FORM_MODE.EDIT && !eventId) {
      return;
    }
    if (mode === FORM_MODE.CREATE) {
      if (isProcessed) return; //ATM creating processed occurrence is not allowed

      setOccurrenceData(applyUserPreferences());
      setFormStatus(OfflineFormStatus.Invalid);
      return;
    }

    setLoading(true);
    if (isRoute) {
      fetchRouted();
    } else {
      if (isProcessed) {
        fetchProcessed();
      } else {
        fetchUnprocessed();
      }
    }
  }, []);

  const applyUserPreferences = () => {
    const dto = {
      provider: 'INTERNAL_INGESTION_' + currentUser.Env.toUpperCase(),
      event_type: 'AVALANCHE EVENT',
      observer: avalancheTechs.options.find((t) => t.value === currentUser.SecurityIdentifierId) || null,
      //status: OfflineFormStatus.Invalid,
      lastUpdated: Date.now(),
      timestamp: new Date(),
    };

    const data = (JSON.parse(currentUser.Preferences) ?? {})?.observations;

    if (!data) return dto;

    if (data.program) {
      dto.avalanche_program = programAreas.find((x) => x.value == data.program && !x.isDisabled);
      dto.avalanche_program && setAreaList(filterList(avalancheAreas, data.program));
    }

    if (dto.avalanche_program && data.area) {
      dto.avalanche_area = avalancheAreas.find((x) => x.value == data.area && !x.isDisabled);
      dto.avalanche_area && setPathsList(filterList(pathsList, data.area));
    }

    if (dto.avalanche_program && dto.avalanche_area && data.path) {
      dto.avalanche_path = pathsList.find((x) => x.value == data.path && !x.isDisabled);
    }

    if (data.triggerType) {
      dto.aval_trig_type_cd = triggerTypes.find((x) => x.value == data.triggerType);
    }

    return dto;
  };

  const setUnprocessedOccurrence = (occurrence) => {
    const loadedOccurrence = mapServerToFormFormat(occurrence);

    setOccurrenceData(loadedOccurrence);
    setFormStatus(loadedOccurrence['status']);

    //avalancheTechs.options.find((t) => t.value === currentUser.SecurityIdentifierId) || null,

    //explosive control event occurrence
    if (loadedOccurrence.aval_cntrl_type_cd && loadedOccurrence.parent_trace_id) {
      setExplosiveControlEvent(true);

      loadedOccurrence['event_timestamp'] = occurrence.internal.event_timestamp;
      loadedOccurrence['aval_trig_type_cd'] =
        loadedOccurrence.aval_trig_type_cd ??
        explosiveTriggerTypes.find((x) => x.value === `X${loadedOccurrence.aval_cntrl_type_cd.value}`) ??
        null;

      if (!loadedOccurrence['observer']) {
        loadedOccurrence['observer'] =
          avalancheTechs.options.find((t) => t.value === currentUser.SecurityIdentifierId) || null;
      }
    }

    //if the prog/area dropdowns have selected values then we need to
    // ensure the filtering is initialized
    if (loadedOccurrence['avalanche_program']) {
      setAreaList(filterList(avalancheAreas, loadedOccurrence['avalanche_program'].value));
    }
    if (loadedOccurrence['avalanche_area']) {
      setPathsList(filterList(avalanchePaths, loadedOccurrence['avalanche_area'].value));
    }

    //set distance from trigger enabled/disabled on load
    if (loadedOccurrence['aval_trig_type_cd']) {
      if (requireTrigDist(loadedOccurrence['aval_trig_type_cd'].value)) {
        setIsTrigDistReq(true);
      } else {
        setIsTrigDistReq(false);
      }
    }
  };

  const fetchUnprocessed = () => {
    AWPLocalDB.avalancheData
      .get(eventId)
      .then((occurrence) => {
        if (!occurrence) {
          {
            occurrence = findEvent(loadedData, eventId);
          }
        } else {
          occurrence.internal.timestamp = new Date(occurrence.internal.timestamp);
        }

        if (occurrence) setUnprocessedOccurrence(occurrence);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchRouted = () => {
    const url = REST_API.INGESTION_CANDIDATE_BY_TRACEID + eventId;
    GetJson(url)
      .then((response) => response.json())
      .then((occurrence) => {
        if (occurrence && !isValueEmpty(occurrence.JsonObject))
          setUnprocessedOccurrence(JSON.parse(occurrence.JsonObject));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const requireTrigDist = (trigCode) => {
    const trigSecondCode = trigCode?.substr(-1);
    return trigCode !== 'Nr' && (trigSecondCode == 'y' || trigSecondCode == 'r');
  };

  const fetchProcessed = () => {
    const url = REST_API.PROCESSED_OCCURRENCE + eventId;

    GetJson(url)
      .then((response) => response.json())
      .then((loadedOccurrence) => {
        afterFetchProcessed(loadedOccurrence);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  function afterFetchProcessed(loadedOccurrence) {
    setFormStatus(loadedOccurrence['status']);
    loadedOccurrence.timestamp = new Date(loadedOccurrence.timestamp);
    loadedOccurrence.lastUpdatetimestamp = loadedOccurrence.lastUpdatetimestamp
      ? getUtcToLocal(loadedOccurrence.lastUpdatetimestamp + '')
      : '';

    if (loadedOccurrence.parent_event_type === EventTypes.AvalancheControl) {
      setExplosiveControlEvent(true);
    }

    loadedOccurrence['event_timestamp'] = new Date(loadedOccurrence.parent_timestamp);

    if (loadedOccurrence['avalanche_program']) {
      setAreaList(filterList(avalancheAreas, loadedOccurrence['avalanche_program'].value));
    }
    if (loadedOccurrence['avalanche_area']) {
      setPathsList(filterList(avalanchePaths, loadedOccurrence['avalanche_area'].value));
    }

    if (loadedOccurrence['aval_trig_type_cd']) {
      setIsTrigDistReq(requireTrigDist(loadedOccurrence['aval_trig_type_cd'].value));
    }

    setOccurrenceData(loadedOccurrence);
    setOriginalValues(loadedOccurrence);
  }

  const filterExplosiveTriggerTypes = () => {
    return explosiveTriggerTypes.filter(
      (x) => x.value === 'Xy' || x.value === 'Xr' || x.value === `X${occurrenceData?.aval_cntrl_type_cd?.value}`,
    );
  };

  const programOnChange = (opt, setFieldValue) => {
    if (isProcessed && mode === FORM_MODE.EDIT) return programOnProcessedChange(opt, setFieldValue);
    setFieldValue('avalanche_area', nullOption, false);
    setFieldValue('avalanche_path', nullOption, false);

    //filter the area list to children of program
    setAreaList(filterList(avalancheAreas, opt.value));

    //path list is unfiltered
    setPathsList(avalanchePaths);
  };
  const programOnProcessedChange = (opt, setFieldValue) => {
    setFieldValue('avalanche_area', nullOption, false);
    setFieldValue('avalanche_path', nullOption, false);
    setAreaList(avalancheAreas.filter((x) => x.parentId === opt.value));

    //path list is unfiltered
    setPathsList(avalanchePaths);
    setFieldValue('avalanche_target', nullOption, false);
    //setPointList([]);
    //setIsDisableTargeDd(true);
  };
  const areaOnChange = (opt, setFieldValue) => {
    if (isProcessed && mode === FORM_MODE.EDIT) return areaOnProcessedChange(opt, setFieldValue);
    // filter the paths list to those for this area
    setPathsList(filterList(avalanchePaths, opt.value));

    //clear path
    setFieldValue('avalanche_path', nullOption, false);

    setFieldValue(
      'avalanche_program',
      avalanchePrograms.find(({ value }) => {
        return value === opt.parentId;
      }),
    );
  };
  const areaOnProcessedChange = (opt, setFieldValue) => {
    // filter the paths list to those for this area
    setPathsList(filterList(avalanchePaths, opt.value));

    resetEventPaths();
    resetEventLocations();
    setFieldValue('avalanche_path', nullOption, false);
    setFieldValue('avalanche_target', nullOption, false);
    //setPointList([]);
    //setIsDisableTargeDd(true);
  };
  const pathOnChange = (opt, setFieldValue, pathListToUse) => {
    if (isProcessed && mode === FORM_MODE.EDIT) return pathOnProcessedChange(opt, setFieldValue);
    //if the user picked a path first, we'll auto select the area/program
    const _area = avalancheAreas.find(({ value }) => {
      return value === opt.parentId;
    });

    //filter the area list to children of program
    setAreaList(filterList(avalancheAreas, _area.parentId));

    //set area value
    setFieldValue('avalanche_area', _area, false);

    //set program state form selected area
    setFieldValue(
      'avalanche_program',
      avalanchePrograms.find(({ value }) => {
        return value === _area.parentId;
      }),
      false,
    );

    //ensure the paths are children of area
    setPathsList(filterList(pathListToUse || pathsList, _area.value));
  };
  const pathOnProcessedChange = (opt, setFieldValue) => {
    if (!eventPaths) return;
    updateEventPath(eventId, opt);
  };
  const updateOccurrence = (setSubmitting, setErrors, dto) => {
    const updatedOccurrence = mapFormToServerFormat(dto);
    AWPLocalDB.avalancheData
      .update(eventId, updatedOccurrence)
      .then(async (updated) => {
        if (updated) {
          toast.success('Event has been updated');
        } else {
          const errors = { id: `Update failed. There was no avalanche data with Id: ${eventId}` };
          setErrors(errors);
          if (errors.occurrence) setOccurrenceErrors({ Occurrence: errors.occurrence });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const createOccurrence = (setSubmitting, setErrors, dto) => {
    const id = uuidv4();
    const createdOccurrence = { id, trace_id: id, type: occurrenceType, ...mapFormToServerFormat(dto) };
    AWPLocalDB.avalancheData
      .add(createdOccurrence)
      .then(async () => {
        toast.success('Event has been created');
        setEventTypeToActOn(createdOccurrence.internal.event_type);
        setEventId(id);
        setMode('edit');
      })
      .catch((error) => {
        const errors = { InsertError: error };
        setErrors(errors);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleSubmit = ({ values, setSubmitting, validateForm, setTouched, setErrors }) => {
    let status = OfflineFormStatus.Valid;
    setFormStatus(status); //update display

    validateForm(values).then((errors) => {
      if (Object.keys(errors).length > 0) {
        setTouched(errors);
        status = OfflineFormStatus.Invalid;
        setFormStatus(status); //update display
      }

      saveForm(values, setSubmitting, setErrors, status);
    });
  };

  const saveForm = (values, setSubmitting, setErrors, status) => {
    setSubmitting(true);

    let dto = {
      ...values,
      userName: currentUser.UserName,
      userType: currentUser.UserType,
      userId: currentUser.SecurityIdentifierId,
      lastUpdated: Date.now(),
      type: occurrenceType,
      status: status,
    };

    if (values.avalanche_path) {
      //if the user used GPS then the distance away was appended to the label of the
      // avalanche path so we need to check for and remove it
      let udpatedLabel = (values.avalanche_path && values.avalanche_path.label) || '';
      udpatedLabel =
        udpatedLabel.endsWith('km)') > 0 ? udpatedLabel.replace(/\([0-9]+.[0-9]+km\)/i, '').trim() : udpatedLabel;
      dto = { ...dto, avalanche_path: { ...values.avalanche_path, label: udpatedLabel } };
    }

    if (mode === FORM_MODE.EDIT) {
      updateOccurrence(setSubmitting, setErrors, dto);
    } else {
      createOccurrence(setSubmitting, setErrors, dto);
    }
  };

  const handleSubmitProcessed = ({ values, setSubmitting, resetForm, setErrors }) => {
    if (originalValues && values) {
      if (isCriticalFieldsUpdated(criticalFields, values, originalValues)) {
        setShowCriticalFieldsModal(true);
        setSubmitting(true);
        setSubmitting(false);
        return;
      }
    }
    setSubmitting(true);
    const body = {};
    const observations = [];

    for (const [key, value] of Object.entries(values)) {
      const field = key.replace(/_measured|_concurrencyControlNumber/g, '');
      const observationField = observationFields.includes(field);

      if (observationField) {
        const observationKey = key.includes('_measured')
          ? 'measured'
          : key.includes('_concurrencyControlNumber')
          ? 'concurrencyControlNumber'
          : 'value';

        const observationIndex = observations.findIndex((o) => o.key === field);

        if (observationIndex === -1) {
          observations.push({
            key: field,
            [observationKey]: value?.value ?? value,
          });
        } else {
          observations[observationIndex] = {
            ...observations[observationIndex],
            [observationKey]: value?.value ?? value,
          };
        }
      } else {
        body[key] = value?.value ?? value;
      }
    }
    //convert from "false" to false -bool for .net 7
    body.incident = body.incident === 'true' ? true : false;
    body.observations = observations;
    if (criticalFields) {
      body.avalanche_path = values.avalanche_path.areaId;
      body.criticalFields = locationCriticalFieldsToEntity(criticalFields, values);
    } else {
      body.criticalFields = null;
    }
    const url = REST_API.PROCESSED_OCCURRENCE + eventId;

    PutJson(url, body)
      .then(async (response) => {
        if (response.ok) {
          toast.success('Processed Event has been updated');
          const loadedOccurrence = await response.json();
          afterFetchProcessed(loadedOccurrence);
          resetCriticalFields();
          resetForm();
        } else if (response.status === 412) {
          const errors = await response.json();
          toast.error('Update failed. Please review field error(s).', { autoClose: 5000 });
          setErrors(errors);
          if (errors.entity) setOccurrenceErrors({ Entity: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleBtnClose = () => {
    // props.resetForm();
    if (fromMenu || isFromControl) navigate(location.pathname, { state: null });
    setMode(FORM_MODE.LIST);
  };
  const handleCriticalSection = (props) => {
    setShowCriticalFieldsModal(false);
    setLoadingCriticalField(true);

    const url = REST_API.PROCESSED_LINKED_EVENTS + eventId;

    GetJson(url)
      .then((response) => response.json())
      .then((entity) => {
        const _entity = entity;
        if (_entity) {
          setCriticalFields(_entity);
          getEventPaths(_entity);
          getEventLocations(_entity);
          setShowCriticalFields(true);
        }
      })
      .finally(() => {
        setLoadingCriticalField(false);
      });
  };
  const resetEventPaths = () => {
    if (!eventPaths) return;
    if (Array.isArray(eventPaths)) {
      let _eventPaths = [];
      eventPaths.map((c) => {
        _eventPaths.push({ eventId: c.eventId, value: null });
      });
      setEventPaths(_eventPaths);
      return;
    }
  };
  const checkEventPaths = (values) => {
    if (!eventPaths) return false;
    if (!values || !values.avalanche_path || !values.avalanche_path || !values.avalanche_path.areaId) return false;
    if (Array.isArray(eventPaths)) {
      const ep = eventPaths.find((c) => {
        return c.eventId !== eventId && !c.value;
      });
      if (ep) return false;
    }
    return true;
  };
  const getEventPathValue = (id) => {
    if (Array.isArray(eventPaths)) {
      const ep = eventPaths.find((c) => {
        return c.eventId === id;
      });
      if (ep) return ep.value;
    }
    return null;
  };
  const updateEventPath = (id, v) => {
    let _eventPaths = [];
    let found = false;
    if (Array.isArray(eventPaths)) {
      eventPaths.map((c) => {
        if (c.eventId === id) {
          _eventPaths.push({ eventId: id, value: { ...v } });
          found = true;
        } else {
          _eventPaths.push(c);
        }
      });
      if (!found) _eventPaths.push({ eventId: id, value: v });
      if (_eventPaths && Array.isArray(_eventPaths) && _eventPaths.length > 0) setEventPaths(_eventPaths);
      return;
    }
  };
  const getEventPaths = (entity) => {
    if (!entity) return;
    let _eventPaths = [];
    let _children = entity.ChildAvalancheProcessedEvents;
    if (Array.isArray(_children)) {
      _children.map((c) => {
        let _gChildren = c.ChildAvalancheProcessedEvents;
        if (Array.isArray(_gChildren)) {
          _gChildren.map((gc) => {
            if (checkEventType(gc.EventTypeNm)) {
              _eventPaths.push({ eventId: gc.EventId, value: null });
            }
          });
        }
        if (checkEventType(c.EventTypeNm)) {
          _eventPaths.push({ eventId: c.EventId, value: null });
        }
      });
    }
    if (checkEventType(entity.EventTypeNm)) {
      _eventPaths.push({ eventId: entity.EventId, value: null });
    }
    if (_eventPaths && Array.isArray(_eventPaths) && _eventPaths.length > 0) setEventPaths(_eventPaths);
  };
  //check if all event types are event path(RC or RO)
  const IsOnlyEventPath = (entity) => {
    if (!entity) return false;
    let _eventLocations = []; //only for RC or Phased = non-eventPaths
    let _eventPathLocations = []; //only for event path(RC or RO)
    let _children = entity.ChildAvalancheProcessedEvents;
    if (Array.isArray(_children)) {
      _children.map((c) => {
        let _gChildren = c.ChildAvalancheProcessedEvents;
        if (Array.isArray(_gChildren)) {
          _gChildren.map((gc) => {
            if (!checkEventType(gc.EventTypeNm)) {
              _eventLocations.push(gc);
            } else {
              _eventPathLocations.push(gc);
            }
          });
        }
        if (!checkEventType(c.EventTypeNm)) {
          _eventLocations.push(c);
        } else {
          _eventPathLocations.push(c);
        }
      });
    }
    if (!checkEventType(entity.EventTypeNm)) {
      _eventLocations.push(entity);
    } else {
      _eventPathLocations.push(entity);
    }
    if (
      _eventLocations.length === 0 &&
      _eventPathLocations &&
      Array.isArray(_eventPathLocations) &&
      _eventPathLocations.length > 0
    )
      return true;
    return false;
  };
  // true = failed /false = passed
  const validCriticalFields = (values) => {
    if (!criticalFields || !occurrenceData) return false;
    const evtType = findEventTypeNmById(criticalFields, occurrenceData.eventId);
    if (evtType && checkEventType(evtType)) {
      if (!criticalFields.ChildAvalancheProcessedEvents || criticalFields.ChildAvalancheProcessedEvents.length === 0)
        return false;
      if (!eventPaths) return true;
      if (!checkEventPaths(values)) return true;
      if (IsOnlyEventPath(criticalFields)) return false;
      return !checkEventLocations();
    }
    return false;
  };

  const resetCriticalFields = () => {
    setCriticalFields(null);
    setEventPaths(null);
    setEventSegment(null);
    setEventFrom(null);
    setEventTo(null);
    setEventLocations(null);
  };

  const resetEventLocations = () => {
    if (!eventLocations) return;
    if (Array.isArray(eventLocations)) {
      let _eventLocations = [];
      eventLocations.map((c) => {
        _eventLocations.push({ eventId: c.eventId, segment: null, from: null, to: null });
      });
      setEventLocations(_eventLocations);
      return;
    }
  };
  //only for RC or Phased = non-eventPaths
  const getEventLocations = (entity) => {
    if (!entity) return;
    let _eventLocations = [];
    let _children = entity.ChildAvalancheProcessedEvents;
    if (Array.isArray(_children)) {
      _children.map((c) => {
        let _gChildren = c.ChildAvalancheProcessedEvents;
        if (Array.isArray(_gChildren)) {
          _gChildren.map((gc) => {
            if (!checkEventType(gc.EventTypeNm)) {
              _eventLocations.push({ eventId: gc.EventId, segment: null, from: null, to: null });
            }
          });
        }
        if (!checkEventType(c.EventTypeNm)) {
          _eventLocations.push({ eventId: c.EventId, segment: null, from: null, to: null });
        }
      });
    }
    if (!checkEventType(entity.EventTypeNm)) {
      _eventLocations.push({ eventId: entity.EventId, segment: null, from: null, to: null });
    }
    if (_eventLocations && Array.isArray(_eventLocations) && _eventLocations.length > 0)
      setEventLocations(_eventLocations);
  };
  const checkEventLocations = () => {
    if (!eventLocations) return false;
    if (Array.isArray(eventLocations)) {
      const ep = eventLocations.find((c) => {
        return !c.segment && !c.from && !c.to;
      });
      if (ep) return false;
    }
    return true;
  };
  const getEventLocationValue = (id, type) => {
    if (Array.isArray(eventLocations)) {
      const ep = eventLocations.find((c) => {
        return c.eventId === id;
      });
      if (ep) {
        if (type === 'segment') return ep.segment;
        if (type === 'from') return ep.from;
        if (type === 'to') return ep.to;
      }
    }
    return null;
  };
  const updateEventLocation = (id, v, type) => {
    let _eventLocations = [];
    if (Array.isArray(eventLocations)) {
      eventLocations.map((c) => {
        if (type === 'segment') _eventLocations.push({ eventId: c.eventId, segment: { ...v }, from: null, to: null });
        if (type === 'from') _eventLocations.push({ eventId: c.eventId, segment: null, from: { ...v }, to: c.to });
        if (type === 'to') _eventLocations.push({ eventId: c.eventId, segment: null, from: c.from, to: { ...v } });
      });

      if (_eventLocations && Array.isArray(_eventLocations) && _eventLocations.length > 0)
        setEventLocations(_eventLocations);
      return;
    }
  };
  const breadCrumb = () => {
    const entity = findEvent(loadedData, eventId);
    const name = entity?.internal.event_type?.label;
    return name ? name : eventId;
  };

  const Buttons = ({ props }) => {
    return mode !== FORM_MODE.VIEW ? (
      <>
        <div className="mr-3" style={{ float: 'left' }}>
          {!showCancelConfirm && (
            <Button
              className="mr-2"
              variant="outline-primary"
              style={{ marginTop: '40px' }}
              onClick={() => {
                setShowCancelConfirm(true);
              }}
            >
              {'Close'}
            </Button>
          )}

          {!showCancelConfirm && isAvalancheTech && (
            <Button
              className="mr-2"
              style={{ marginTop: '40px' }}
              type="button"
              variant="primary"
              onClick={() => (isProcessed ? handleSubmitProcessed(props) : handleSubmit(props))}
              disabled={
                isProcessed
                  ? !props.isValid || !props.dirty || props.isSubmitting || validCriticalFields(props.values)
                  : props.isSubmitting
              }
            >
              {props.isSubmitting ? (
                <>
                  <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                <span>{buttonLabel}</span>
              )}
            </Button>
          )}
          {showCancelConfirm && (
            <AwpYesNoDialog
              action={() => handleBtnClose(props)}
              setShowYesNoConfirm={setShowCancelConfirm}
              msg={'Are you sure you want to close?'}
              props={{}}
            />
          )}
        </div>
      </>
    ) : (
      <Button
        className="mr-2"
        style={{ marginTop: '40px' }}
        variant="outline-primary"
        onClick={() => {
          handleBtnClose(props);
        }}
      >
        {'Close'}
      </Button>
    );
  };

  Buttons.propTypes = {
    title: PropTypes.string,
    props: PropTypes.any,
    resetForm: PropTypes.func,
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    dirty: PropTypes.bool,
  };

  return (
    <>
      {loading || occurrenceData === {} ? (
        <Container>
          <Spinner animation="border" />
        </Container>
      ) : (
        <>
          <div style={{ paddingBottom: '10px' }}>
            <span className="awp-text-btn" onClick={() => setMode(FORM_MODE.LIST)}>
              {breadcrumbName}
            </span>
            <span className="mx-3">&gt;</span>
            <span>{breadCrumb()}</span>
          </div>
          <Formik
            enableReinitialize
            validateOnMount={false}
            validateOnBlur
            validateOnChange
            initialTouched={mapObject(occurrenceData, true)}
            initialValues={{
              ...occurrenceData,
            }}
            validationSchema={validationSchema}
            onSubmit={isProcessed ? handleSubmitProcessed : handleSubmit}
          >
            {(props) => (
              <>
                {/* {props.values && <pre>{JSON.stringify(props.values, null, 2)}</pre>} */}
                <div className="d-flex">
                  <h1 className="flex-grow-1">{title}</h1>
                </div>
                <Form>
                  <Card
                    border="light"
                    className="page-card"
                    style={{ border: '0px', boxShadow: '0px 0px 0px #000000' }}
                  >
                    <Card.Body className="pr-pl-0">
                      {(mode === FORM_MODE.EDIT || mode === FORM_MODE.VIEW) && occurrenceData && isProcessed && (
                        <div className="mb-3">
                          <FormTextReadOnly label="Created By" textValue={occurrenceData.createUserId} />
                          <FormTextReadOnly label="Last Updated" textValue={occurrenceData.lastUpdatetimestamp} />
                          <FormTextReadOnly label="Last Updated By" textValue={occurrenceData.lastUpdateUserId} />
                        </div>
                      )}

                      <FormSelect
                        className="avalanche-select"
                        readOnly={readOnly}
                        name="avalanche_program"
                        id="avalanche_program"
                        label="Program"
                        useMargin={true}
                        postChange={(e) => {
                          programOnChange(e, props.setFieldValue);
                        }}
                        options={avalanchePrograms}
                        isMulti={false}
                      />

                      <FormSelect
                        className="avalanche-select"
                        readOnly={readOnly}
                        name="avalanche_area"
                        id="avalanche_area"
                        label="Avalanche Area"
                        useMargin={true}
                        postChange={(e) => {
                          areaOnChange(e, props.setFieldValue);
                        }}
                        options={areaList}
                        isMulti={false}
                      />

                      <DropdownWithGeolocate
                        readOnly={readOnly}
                        name="avalanche_path"
                        id="avalanche_path"
                        label="Path (required)"
                        useMargin={true}
                        postChange={(e) => {
                          pathOnChange(e, props.setFieldValue);
                        }}
                        options={pathsList}
                        isMulti={false}
                        buttonAction={(e) => {
                          getPosition(e, props.setFieldValue, props.setFieldTouched);
                        }}
                        disableGPS={explosiveControlEvent}
                        focus={isFromControl || (isDuplicated && mode === FORM_MODE.EDIT)}
                        warningMessage={isFromControl}
                      />

                      <FormSelect
                        readOnly={readOnly}
                        name="aval_size_cd"
                        id="aval_size_cd"
                        label="Avalanche Size (required)"
                        useMargin={true}
                        options={avalancheSizeOptions}
                        isMulti={false}
                        useSearchByValue={true}
                      />

                      <FormSelect
                        readOnly={readOnly}
                        name="aval_trig_type_cd"
                        id="aval_trig_type_cd"
                        label="Trigger Type (required)"
                        useMargin={true}
                        options={
                          explosiveControlEvent
                            ? groupTrigTypes(filterExplosiveTriggerTypes())
                            : groupTrigTypes(nonExplosiveTriggerTypes)
                        }
                        isMulti={false}
                        postChange={(e) => {
                          if (requireTrigDist(e.value)) {
                            setIsTrigDistReq(true);
                          } else {
                            setTimeout(() => props.setFieldValue('aval_trig_dist', '', false)); // required delay: WEAV-10272
                            delete props.values.aval_trig_dist;
                            setIsTrigDistReq(false);
                          }
                        }}
                      />

                      <FormNumberInput
                        readOnly={readOnly}
                        label={isTrigDistReq ? 'Distance from Trigger [m] (required)' : 'Distance from Trigger [m]'}
                        disabled={!isTrigDistReq}
                        name="aval_trig_dist"
                        useMargin={true}
                        id="aval_trig_dist"
                      />

                      <div className="customWidthDatePicker">
                        <FormDatePicker
                          readOnly={readOnly}
                          label="Occurrence Date/Time (required)"
                          id="timestamp"
                          name="timestamp"
                          useMargin={true}
                          showTimeSelect={true}
                          showTime={{ isEnabled: true }}
                          timeFormat="HH:mm"
                          timeIntervals={30}
                          dateFormat={['yyyy-MM-dd HH:mm']}
                        />
                      </div>

                      <FormNumberInput
                        readOnly={readOnly}
                        useMargin={true}
                        label="Time Range +/- hrs (required)"
                        name="uncertainty_hours"
                        id="uncertainty_hours"
                      />

                      <FormSelect
                        readOnly={readOnly}
                        name="observer"
                        id="observer"
                        useMargin={true}
                        label="Observer (required)"
                        options={avalancheTechs.options}
                        isMulti={false}
                      />
                      <FormNumberInput
                        readOnly={readOnly}
                        label="Toe Distance of Mass [m] (required)"
                        warningMessage={showRoadImpactMessage ? 'Impact to Road fields are now required' : ''}
                        name="aval_mass_toe_dist"
                        useMargin={true}
                        id="aval_mass_toe_dist"
                        postChange={(value) => {
                          props.setFieldTouched('aval_rd_cover_length');
                          props.setFieldTouched('aval_rd_dpth_max');
                          props.setFieldTouched('aval_rd_dpth');
                          props.validateField('aval_rd_cover_length');
                          props.setFieldValue('aval_mass_toe_dist', value, true);
                          setShowRoadImpactMessage(value > 0 ? true : false);
                        }}
                      />
                      <FormCheckbox
                        readOnly={readOnly}
                        id="aval_mass_toe_dist_measured"
                        name="aval_mass_toe_dist_measured"
                        useMargin={true}
                        label="Measured"
                        value="false"
                      />
                      <FormRadioGroup
                        label="Incident (required)"
                        name="incident"
                        id="incident"
                        readOnly={readOnly || isProcessed}
                        textValue={
                          props.values.incident + '' === 'false'
                            ? 'N'
                            : props.values.incident + '' === 'true'
                            ? 'Y'
                            : ''
                        }
                      >
                        <Row>
                          <Col sm={1} md={1} lg={1}>
                            <FormRadio id="incidentYes" label="Yes" name="incident" value="true" readOnly={readOnly} />
                          </Col>
                          <Col sm={1} md={1} lg={1}>
                            <FormRadio id="incidentNo" label="No" name="incident" value="false" readOnly={readOnly} />
                          </Col>
                        </Row>
                      </FormRadioGroup>

                      <OccurrenceOptionalFields
                        readOnly={readOnly}
                        props={props}
                        eventProperties={avalancheEventProperties}
                        useMargin={true}
                      />

                      <FormTextArea readOnly={readOnly} label="Comment" id="comment" name="comment" rows={3} />
                      {mode !== FORM_MODE.CREATE ? (
                        <div>
                          <b>Form Status:</b>{' '}
                          <span
                            style={formStatus === OfflineFormStatus.Invalid ? { color: 'red' } : { color: 'black' }}
                          >
                            {formStatus}
                          </span>
                        </div>
                      ) : null}
                      <Buttons title=" " props={props} />
                      <AwpYesNoModal
                        showYesNoModal={showSaveConfirm}
                        setShowYesNoModal={setShowSaveConfirm}
                        onClickYes={() => {
                          setShowSaveConfirm(false);
                          handleSubmitProcessed(props);
                        }}
                        title="Save"
                        body="A change in Date/Time of Control events cascades to its related child Xn Occurrence. Do you still want to save?"
                      />
                      {loadingCriticalField && (
                        <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                      )}
                      {!loadingCriticalField &&
                        !showCriticalFieldsModal &&
                        showCriticalFields &&
                        eventId > 0 &&
                        criticalFields && (
                          <div style={{ display: 'block', clear: 'both', paddingTop: '1rem' }}>
                            <ProcessedCriticalFieldsEdit
                              dto={criticalFields}
                              setCriticalFields={setCriticalFields}
                              criticalFields={criticalFields}
                              updatedDto={props.values}
                              eventPaths={eventPaths}
                              updateEventPath={updateEventPath}
                              getEventPathValue={getEventPathValue}
                              eventSegment={eventSegment}
                              setEventSegment={setEventSegment}
                              eventFrom={eventFrom}
                              setEventFrom={setEventFrom}
                              eventTo={eventTo}
                              setEventTo={setEventTo}
                              isParent={true}
                              eventLocations={eventLocations}
                              updateEventLocation={updateEventLocation}
                              getEventLocationValue={getEventLocationValue}
                            />
                            <Row style={{ height: '1rem' }} />
                          </div>
                        )}
                      {!loadingCriticalField &&
                        !showCriticalFieldsModal &&
                        showCriticalFields &&
                        eventId > 0 &&
                        criticalFields && (
                          <div style={{ display: 'block', clear: 'both', paddingTop: '1rem' }}>
                            {criticalFields &&
                              criticalFields.ChildAvalancheProcessedEvents &&
                              Object.keys(criticalFields.ChildAvalancheProcessedEvents).map((d, idx) => {
                                return (
                                  <>
                                    <ProcessedCriticalFieldsEdit
                                      key={idx}
                                      dto={criticalFields.ChildAvalancheProcessedEvents[d]}
                                      setCriticalFields={setCriticalFields}
                                      criticalFields={criticalFields}
                                      updatedDto={props.values}
                                      eventPaths={eventPaths}
                                      updateEventPath={updateEventPath}
                                      getEventPathValue={getEventPathValue}
                                      eventSegment={eventSegment}
                                      setEventSegment={setEventSegment}
                                      eventFrom={eventFrom}
                                      setEventFrom={setEventFrom}
                                      eventTo={eventTo}
                                      setEventTo={setEventTo}
                                      isParent={false}
                                      eventLocations={eventLocations}
                                      updateEventLocation={updateEventLocation}
                                      getEventLocationValue={getEventLocationValue}
                                    />
                                    <Row style={{ height: '1rem' }} />
                                  </>
                                );
                              })}
                          </div>
                        )}
                    </Card.Body>
                  </Card>
                </Form>
                <AwpYesNoModal
                  showYesNoModal={showCriticalFieldsModal}
                  setShowYesNoModal={setShowCriticalFieldsModal}
                  onClickYes={() => {
                    handleCriticalSection(props);
                  }}
                  title="Warning"
                  body="Changing the location information will result in the related elements needing to be set again. Are you sure you want to proceed?"
                />
              </>
            )}
          </Formik>
          <AwpValidationErrorModal setErrors={setOccurrenceErrors} errors={occurrenceErrors} />
        </>
      )}
    </>
  );
};

OccurrenceEdit.propTypes = {
  setMode: PropTypes.func,
  mode: PropTypes.string,
  setEventId: PropTypes.func,
  eventId: PropTypes.any,
  setLoading: PropTypes.func,
  loading: PropTypes.bool,
  setFieldValue: PropTypes.func,
  validateField: PropTypes.func,
  setFieldTouched: PropTypes.func,
  values: PropTypes.any,
  resetForm: PropTypes.func,
  fullData: PropTypes.array,
  setEventTypeToActOn: PropTypes.func,
  isProcessed: PropTypes.bool,
  isRoute: PropTypes.bool,
  isDuplicated: PropTypes.bool,
};

export default OccurrenceEdit;
